/*
    The ground's materials (0.98.7, the owner, 1 November 2026: "New ground textures"): the parts without Pixi
    (tests/src/update0987.test.ts). The buildings' flat patches of ground (a road, a parking lot, a yard, a strip of
    sand: `graphics`, `groundGraphics`, `terrainGraphics` in definitions/buildings.ts, each a colour over a shape) are
    drawn with a painted material in that colour instead of the colour alone (war/groundMaterials.ts): which material
    is told from the colour, the way the maps' makers used them (grey asphalt and concrete, brown dirt, tan sand, green
    grass); a stripe of road paint, a yellow line, a red or a purple patch stays the plain colour it is. The natural
    ground (the fields, the mud, the beaches, the tracks) is the ground shader's (war/warShaders.ts), from the atlas
    of four materials tools/ground/paint_ground.py paints.
*/

export type FillMaterial = "asphalt" | "concrete" | "dirt" | "sand" | "grass";

/** the painted tiles' mean colours (tools/ground/paint_ground.py prints them): a colour's fill is the tile tinted to it */
export const FILL_MEANS: Readonly<Record<FillMaterial, readonly [number, number, number]>> = Object.freeze({
    asphalt: [0.700, 0.700, 0.701],
    concrete: [0.700, 0.700, 0.700],
    dirt: [0.811, 0.682, 0.503],
    sand: [0.741, 0.704, 0.566],
    grass: [0.668, 0.773, 0.409]
});

/** one tile of each material covers this many game units (a player is 2.25 round) */
export const FILL_TILE_UNITS: Readonly<Record<FillMaterial, number>> = Object.freeze({
    asphalt: 22,
    concrete: 16,
    dirt: 14,
    sand: 18,
    grass: 20
});

/** the materials' pictures (client/public/img/ground/), each with its normal map beside it */
export const FILL_FILES: Readonly<Record<FillMaterial, string>> = Object.freeze({
    asphalt: "asphalt",
    concrete: "concrete",
    dirt: "dirt",
    sand: "sand",
    grass: "grass"
});

/** the natural ground's atlas: four tiles of 1008 with an 8 pixel wrapped border each, in a 2048 picture */
export const GROUND_ATLAS = Object.freeze({
    size: 2048,
    tile: 1008,
    pad: 8,
    /** each material's tile: its corner in the atlas (0 or 1 each way) */
    tiles: Object.freeze({ field: [0, 0], mud: [1, 0], sand: [0, 1], gravel: [1, 1] } as const),
    /** one tile covers this many game units, at the two sizes the shader blends (the big breaks the small's repeat) */
    units: Object.freeze({ field: 21, mud: 25, sand: 19, gravel: 11 } as const),
    /**
     * its mips are clamped at this level (0.98.7's audit): a tile's wrap in the shader makes the card reach for the
     * smallest mips along the seam, where the four materials run together; at this one a tile's border (8 pixels) is
     * still a pixel wide
     */
    mostLod: 3,
    albedo: "ground_albedo",
    normal: "ground_normal"
});

/** a shape too narrow to be a patch of ground: a painted line, a kerb (game units) */
export const THIN = 2.5;
/** a shape with less area than this is a marking, not ground (game units squared) */
export const SMALL = 40;

/** a colour as the definitions write it (0xrrggbb, or "#rgb", "#rrggbb"; one with an alpha is no material) to r, g, b in 0..1, or none */
export function parseColor(color: number | string): readonly [number, number, number] | undefined {
    if (typeof color === "number") {
        if (!Number.isFinite(color) || color < 0 || color > 0xffffff) return undefined;
        return [((color >> 16) & 0xff) / 255, ((color >> 8) & 0xff) / 255, (color & 0xff) / 255];
    }
    const hex = color.trim().replace(/^#/u, "");
    if (hex.length === 3) return [parseInt(hex[0] + hex[0], 16) / 255, parseInt(hex[1] + hex[1], 16) / 255, parseInt(hex[2] + hex[2], 16) / 255];
    if (hex.length === 6) return [parseInt(hex.slice(0, 2), 16) / 255, parseInt(hex.slice(2, 4), 16) / 255, parseInt(hex.slice(4, 6), 16) / 255];
    return undefined;
}

/** a colour's hue (degrees, 0..360), saturation (0..1) and luma (0..1) */
export function hsl(r: number, g: number, b: number): { hue: number, saturation: number, luma: number } {
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const chroma = max - min;
    let hue = 0;
    if (chroma > 1e-6) {
        if (max === r) hue = ((g - b) / chroma) % 6;
        else if (max === g) hue = (b - r) / chroma + 2;
        else hue = (r - g) / chroma + 4;
        hue *= 60;
        if (hue < 0) hue += 360;
    }
    const saturation = max > 1e-6 ? chroma / max : 0;
    return { hue, saturation, luma: 0.299 * r + 0.587 * g + 0.114 * b };
}

/**
 * the material a flat patch of ground of this colour is drawn with, or none when it's a marking: too narrow or small
 * (`width` and `height` its shape's extent in game units), paint (the whites, the yellows), or a colour no ground
 * material is (a red, a purple, a blue)
 */
export function materialOf(color: number | string, width: number, height: number): FillMaterial | undefined {
    if (Math.min(width, height) < THIN || width * height < SMALL) return undefined;
    const rgb = parseColor(color);
    if (!rgb) return undefined;
    const { hue, saturation, luma } = hsl(rgb[0], rgb[1], rgb[2]);
    if (saturation < 0.25) {
        if (luma > 0.78) return undefined;
        return luma < 0.42 ? "asphalt" : "concrete";
    }
    if (hue >= 18 && hue <= 42) return luma >= 0.5 ? "sand" : "dirt";
    if (hue > 60 && hue < 165) return "grass";
    return undefined;
}

/** the tint a material's tile is drawn with so it averages the colour asked for (each channel, never past white) */
export function fillTint(material: FillMaterial, color: number | string): number {
    const rgb = parseColor(color) ?? [0.5, 0.5, 0.5];
    const mean = FILL_MEANS[material];
    const channel = (i: number): number => Math.round(Math.min(1, rgb[i] / Math.max(0.05, mean[i])) * 255);
    return (channel(0) << 16) | (channel(1) << 8) | channel(2);
}
